import { useCallback, useMemo, useReducer } from "react";
import { IFormBuilderForm } from "../model/formBuilderForm";
import { IFormBuilderSelectedEntity } from "../selectedEntity/formBuilderSelectedEntity";
import { TFormBuilderEntity, TFormBuilderEntityMap } from "../model/formBuilderEntity";
import { IAddEntityActionPayload } from "./actions/addEntity";
import { IDeleteEntityActionPayload } from "./actions/deleteEntity";
import { applyHistoryChange, applyUndoHistoryChange, TFormBuilderControllerAction, TFormBuilderControllerHistoryAction } from "./formBuilderControllerAction";
import { IMoveRowActionPayload } from "./actions/moveRow";
import { ISelectEntityActionPayload } from "./actions/selectEntity";
import { IUpdateSelectedEntityActionPayload } from "./actions/updateSelectedEntity";
import { IUpdateOverlayTargetActionPayload } from "./actions/updateOverlayTarget";
import { IUpdateRowSettingsActionPayload } from "./actions/updateRowSettings";
import { IMoveItemActionPayload } from "./actions/moveItem";
import { IUpdateItemSettingsActionPayload } from "./actions/updateItemSettings";
import { IUpdateFormSettingsActionPayload } from "./actions/updateFormSettings";
import { TFormBuilderEntityLifecycleSnapshot } from "./actionHelpers/entityLifecycleSnapshot";
import { TFormBuilderOverlayTarget } from "../designSpace/overlay/FormBuilderOverlay";
import { TFormBuilderItem } from "../model/item/formBuilderItem";

interface IFormBuilderControllerState {
  form: IFormBuilderForm;
  entityMap: TFormBuilderEntityMap;
  overlayTarget: TFormBuilderOverlayTarget;
  selectedEntity: IFormBuilderSelectedEntity;
  history: TFormBuilderControllerHistoryAction[];
  historyIndex: number;
}

function populateEntityMap(form: IFormBuilderForm): TFormBuilderEntityMap {
  const map: TFormBuilderEntityMap = new Map();

  for (const row of form.children) {
    map.set(row.key, row);

    for (const item of row.children) {
      map.set(item.key, item);
    }
  }

  return map;
}

function formControllerInitializer(form: IFormBuilderForm): IFormBuilderControllerState {
  return {
    form,
    entityMap: populateEntityMap(form),
    overlayTarget: null,
    selectedEntity: null,
    history: [],
    historyIndex: -1,
  };
}

function containsSelectedEntity(entity: TFormBuilderEntity, selectedEntityKey: string): boolean {
  if (entity == null || selectedEntityKey == null) {
    return false;
  }

  if (entity.key === selectedEntityKey) {
    return true;
  }

  if (entity.type === "row") {
    return entity.children.some((child: TFormBuilderItem) => containsSelectedEntity(child, selectedEntityKey));
  } else {
    return false;
  }
}

function snapshotContainsSelectedEntity(snapshot: TFormBuilderEntityLifecycleSnapshot, selectedEntityKey: string) {
  if (snapshot.type === "row") {
    return containsSelectedEntity(snapshot.entity, selectedEntityKey);
  } else if (snapshot.type === "item") {
    return containsSelectedEntity(snapshot.entity, selectedEntityKey) || containsSelectedEntity(snapshot.parentRow, selectedEntityKey);
  } else {
    return false;
  }
}

function handleHistoryAction(state: IFormBuilderControllerState, action: TFormBuilderControllerHistoryAction): IFormBuilderControllerState {
  const canUndo = state.history.length > 0 && state.historyIndex >= 0;
  const canRedo = state.history.length > 0 && state.historyIndex < state.history.length - 1;

  if (action.type === "UNDO") {
    if (!canUndo) {
      return state;
    }

    const actionToUndo = state.history[state.historyIndex];

    let updatedSelectedEntity = state.selectedEntity;

    if (actionToUndo.type === "ADD_ENTITY" && snapshotContainsSelectedEntity(actionToUndo.payload.snapshot, state.selectedEntity?.key)) {
      updatedSelectedEntity = null;
    } else if (actionToUndo.type === "MOVE_ITEM" && actionToUndo.payload.key === state.selectedEntity?.key) {
      const context = actionToUndo.payload.context;
      
      const rowKey = context.type === "within-row" ? context.rowKey : context.prevRowKey;
      
      updatedSelectedEntity = {
        type: "item",
        key: actionToUndo.payload.key,
        rowKey,
        editingMode: {
          type: "none",
        },
      };
    } else if (actionToUndo.type === "MOVE_ROW" && actionToUndo.payload.key === state.selectedEntity?.key) {
      updatedSelectedEntity = {
        type: "row",
        key: actionToUndo.payload.key,
        editingMode: {
          type: "none",
        },
      };
    }

    return {
      ...state,
      form: applyUndoHistoryChange(state.form, actionToUndo),
      historyIndex: state.historyIndex - 1,
      selectedEntity: updatedSelectedEntity,
    };
  }

  if (action.type === "REDO") {
    if (!canRedo) {
      return state;
    }
    
    const nextActionIndex = state.historyIndex + 1;
    const actionToRedo = state.history[nextActionIndex];

    let updatedSelectedEntity = state.selectedEntity;

    if (actionToRedo.type === "DELETE_ENTITY" && snapshotContainsSelectedEntity(actionToRedo.payload.snapshot, state.selectedEntity?.key)) {
      updatedSelectedEntity = null;
    } else if (actionToRedo.type === "MOVE_ITEM" && actionToRedo.payload.key === state.selectedEntity?.key) {
      const context = actionToRedo.payload.context;

      const rowKey = context.type === "within-row" ? context.rowKey : context.nextRowKey;
      
      updatedSelectedEntity = {
        type: "item",
        key: actionToRedo.payload.key,
        rowKey,
        editingMode: {
          type: "none",
        },
      };
    } else if (actionToRedo.type === "MOVE_ROW" && actionToRedo.payload.key === state.selectedEntity?.key) {
      updatedSelectedEntity = {
        type: "row",
        key: actionToRedo.payload.key,
        editingMode: {
          type: "none",
        },
      };
    }

    return {
      ...state,
      form: applyHistoryChange(state.form, actionToRedo),
      historyIndex: nextActionIndex,
      selectedEntity: updatedSelectedEntity,
    };
  }

  let updatedHistory = state.history;

  if (canRedo) {
    updatedHistory = state.history.slice(0, state.historyIndex + 1);
  }

  let updatedSelectedEntity: IFormBuilderSelectedEntity = state.selectedEntity;

  if (
    action.type === "DELETE_ENTITY" &&
    snapshotContainsSelectedEntity(action.payload.snapshot, state.selectedEntity?.key)
  ) {
    updatedSelectedEntity = null;
  } else if (action.type === "MOVE_ITEM") {
    if (action.payload.context.type === "within-row") {
      updatedSelectedEntity = {
        ...state.selectedEntity,
        editingMode: {
          type: "none",
        },
      };
    } else if (action.payload.context.type === "to-new-row" || action.payload.context.type === "to-other-row") {
      updatedSelectedEntity = {
        type: "item",
        rowKey: action.payload.context.nextRowKey,
        key: action.payload.key,
        editingMode: {
          type: "none",
        },
      };
    } else {
      updatedSelectedEntity = null;
    }
  } else if (action.type === "MOVE_ROW") {
    updatedSelectedEntity = {
      ...state.selectedEntity,
      editingMode: {
        type: "none",
      },
    };
  }

  const updatedState: IFormBuilderControllerState = {
    ...state,
    form: applyHistoryChange(state.form, action),
    history: [...updatedHistory, action],
    historyIndex: state.historyIndex + 1,
    selectedEntity: updatedSelectedEntity,
  };

  return updatedState;
}

function formControllerReducer(state: IFormBuilderControllerState, action: TFormBuilderControllerAction): IFormBuilderControllerState {
  if (action.type === "SELECT_ENTITY") {
    return {
      ...state,
      selectedEntity: action.payload.selectedEntity,
    };
  }

  if (action.type === "UPDATE_OVERLAY_TARGET") {
    return {
      ...state,
      overlayTarget: action.payload.overlayTarget,
    };
  }

  if (action.type === "UPDATE_SELECTED_ENTITY") {
    if (state.selectedEntity?.type === "row" && action.payload.selectedEntityUpdates?.type === "row") {
      return {
        ...state,
        selectedEntity: {
          ...state.selectedEntity,
          ...action.payload.selectedEntityUpdates,
        },
      };
    } else if (state.selectedEntity?.type === "item" && action.payload.selectedEntityUpdates?.type === "item") {
      return {
        ...state,
        selectedEntity: {
          ...state.selectedEntity,
          ...action.payload.selectedEntityUpdates,
        },
      };
    } else {
      return state;
    }
  }

  const updatedState = handleHistoryAction(state, action);

  return {
    ...updatedState,
    entityMap: populateEntityMap(updatedState.form),
  };
}

type TFormBuilderControllerAddEntity = (payload: IAddEntityActionPayload) => void;
type TFormBuilderControllerDeleteEntity = (payload: IDeleteEntityActionPayload) => void;
type TFormBuilderControllerMoveItem = (payload: IMoveItemActionPayload) => void;
type TFormBuilderControllerMoveRow = (payload: IMoveRowActionPayload) => void;
type TFormBuilderControllerRedo = () => void;
type TFormBuilderControllerSelectEntity = (payload: ISelectEntityActionPayload) => void;
type TFormBuilderControllerUndo = () => void;
type TFormBuilderControllerUpdateFormSettings = (payload: IUpdateFormSettingsActionPayload) => void;
type TFormBuilderControllerUpdateItemSettings = (payload: IUpdateItemSettingsActionPayload) => void;
type TFormBuilderControllerUpdateOverlayTarget = (payload: IUpdateOverlayTargetActionPayload) => void;
type TFormBuilderControllerUpdateRowSettings = (payload: IUpdateRowSettingsActionPayload) => void;
type TFormBuilderControllerUpdateSelectedEntity = (payload: IUpdateSelectedEntityActionPayload) => void;

export interface IFormBuilderController {
  form: IFormBuilderForm;
  entityMap: TFormBuilderEntityMap;
  overlayTarget: TFormBuilderOverlayTarget;
  selectedEntity: IFormBuilderSelectedEntity;
  addEntity: TFormBuilderControllerAddEntity;
  deleteEntity: TFormBuilderControllerDeleteEntity;
  moveItem: TFormBuilderControllerMoveItem;
  moveRow: TFormBuilderControllerMoveRow;
  redo: TFormBuilderControllerRedo;
  selectEntity: TFormBuilderControllerSelectEntity;
  undo: TFormBuilderControllerUndo;
  updateFormSettings: TFormBuilderControllerUpdateFormSettings;
  updateItemSettings: TFormBuilderControllerUpdateItemSettings;
  updateOverlayTarget: TFormBuilderControllerUpdateOverlayTarget;
  updateRowSettings: TFormBuilderControllerUpdateRowSettings;
  updateSelectedEntity: TFormBuilderControllerUpdateSelectedEntity;
}

export function useFormBuilderController(form: IFormBuilderForm): IFormBuilderController {
  const [state, dispatch] = useReducer(formControllerReducer, form, formControllerInitializer);

  const addEntity: TFormBuilderControllerAddEntity = useCallback(payload => {
    dispatch({ type: "ADD_ENTITY", payload });
  }, []);

  const deleteEntity: TFormBuilderControllerDeleteEntity = useCallback(payload => {
    dispatch({ type: "DELETE_ENTITY", payload });
  }, []);

  const moveItem: TFormBuilderControllerMoveItem = useCallback(payload => {
    dispatch({ type: "MOVE_ITEM", payload });
  }, []);

  const moveRow: TFormBuilderControllerMoveRow = useCallback(payload => {
    dispatch({ type: "MOVE_ROW", payload });
  }, []);

  const redo: TFormBuilderControllerRedo = useCallback(() => {
    dispatch({ type: "REDO" });
  }, []);

  const selectEntity: TFormBuilderControllerSelectEntity = useCallback(payload => {
    dispatch({ type: "SELECT_ENTITY", payload });
  }, []);

  const undo: TFormBuilderControllerUndo = useCallback(() => {
    dispatch({ type: "UNDO" });
  }, []);

  const updateFormSettings: TFormBuilderControllerUpdateFormSettings = useCallback(payload => {
    dispatch({ type: "UPDATE_FORM_SETTINGS", payload });
  }, []);

  const updateItemSettings: TFormBuilderControllerUpdateItemSettings = useCallback(payload => {
    dispatch({ type: "UPDATE_ITEM_SETTINGS", payload });
  }, []);

  const updateOverlayTarget: TFormBuilderControllerUpdateOverlayTarget = useCallback(payload => {
    dispatch({ type: "UPDATE_OVERLAY_TARGET", payload });
  }, []);

  const updateRowSettings: TFormBuilderControllerUpdateRowSettings = useCallback(payload => {
    dispatch({ type: "UPDATE_ROW_SETTINGS", payload });
  }, []);

  const updateSelectedEntity: TFormBuilderControllerUpdateSelectedEntity = useCallback(payload => {
    dispatch({ type: "UPDATE_SELECTED_ENTITY", payload });
  }, []);

  const formBuilderController: IFormBuilderController = useMemo(() => {
    return {
      form: state.form,
      entityMap: state.entityMap,
      overlayTarget: state.overlayTarget,
      selectedEntity: state.selectedEntity,
      addEntity,
      deleteEntity,
      moveItem,
      moveRow,
      redo,
      selectEntity,
      undo,
      updateFormSettings,
      updateItemSettings,
      updateOverlayTarget,
      updateRowSettings,
      updateSelectedEntity,
    };
  }, [
    state.form,
    state.entityMap,
    state.overlayTarget,
    state.selectedEntity,
    addEntity,
    deleteEntity,
    moveItem,
    moveRow,
    redo,
    selectEntity,
    undo,
    updateFormSettings,
    updateItemSettings,
    updateOverlayTarget,
    updateRowSettings,
    updateSelectedEntity,
  ]);

  return formBuilderController;
}
